import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Grid, Chip, Alert, AlertTitle, Fade,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SpeedIcon from '@mui/icons-material/Speed';
import SectionHeader from './SectionHeader';
import DetailSection from './DetailSection';
import { GradientBackground } from './styled';

const VolumeIntensityDetail = ({ volumeMetrics, intensityMetrics, cardioMetrics, biomechanics, onBack }) => {
  const theme = useTheme();
  const dominantModality = biomechanics?.dominant_modality || 'mixed';

  // Strength Metrics
  const totalVolume = volumeMetrics?.total_volume_load ?? biomechanics?.total_volume_load ?? 0;
  const totalWorkingVolume = volumeMetrics?.total_working_volume_load ?? 0;
  const totalSets = volumeMetrics?.total_sets ?? 0;
  const totalWorkingSets = volumeMetrics?.total_working_sets ?? 0;
  const totalWarmupSets = volumeMetrics?.total_warmup_sets ?? 0;
  const totalReps = volumeMetrics?.total_reps ?? 0;
  const isWarmupOnly = totalSets > 0 && totalWorkingSets === 0;
  const displayVolume = totalWorkingVolume > 0 ? totalWorkingVolume : totalVolume;

  // Cardio Metrics
  const totalDistance = cardioMetrics?.total_distance_meters || 0;
  const totalCalories = cardioMetrics?.estimated_calories_burned || 0;
  const avgHeartRate = cardioMetrics?.average_heart_rate || 0;
  const displayDistance = totalDistance > 1000 ? (totalDistance / 1000).toFixed(2) : totalDistance;
  const distanceUnit = totalDistance > 1000 ? 'km' : 'm';

  // Determine which metrics to display in the header based on data presence
  const displayMetrics = [];
  
  if (displayVolume > 0) {
    displayMetrics.push({
      label: 'Total Volume',
      value: displayVolume.toLocaleString(),
      unit: volumeMetrics?.volume_unit || 'lbs',
      color: 'primary.main'
    });
  }
  
  if (totalDistance > 0) {
    displayMetrics.push({
      label: 'Total Distance',
      value: displayDistance,
      unit: distanceUnit,
      color: 'error.main'
    });
  }

  if (totalSets > 0) {
    displayMetrics.push({
      label: 'Sets',
      value: isWarmupOnly ? totalWarmupSets : totalWorkingSets || totalSets,
      subtext: !isWarmupOnly && totalWarmupSets > 0 ? `+${totalWarmupSets} warmup` : null,
      color: 'text.primary'
    });
  }

  if (totalCalories > 0) {
    displayMetrics.push({
      label: 'Calories',
      value: totalCalories,
      unit: 'kcal',
      color: 'warning.main'
    });
  }

  if (avgHeartRate > 0) {
    displayMetrics.push({
      label: 'Avg Heart Rate',
      value: avgHeartRate,
      unit: 'bpm',
      color: 'error.main'
    });
  }

  if (totalReps > 0) {
    displayMetrics.push({
      label: 'Total Reps',
      value: totalReps,
      color: 'text.primary'
    });
  }

  // Determine grid sizing based on number of metrics
  const gridXs = 12;
  const gridSm = displayMetrics.length > 2 ? 4 : 6;
  const gridMd = displayMetrics.length > 3 ? 3 : (displayMetrics.length > 2 ? 4 : 6);

  return (
    <Fade in timeout={300}>
      <Box>
        <SectionHeader icon={<FitnessCenterIcon />} title="Work Output & Intensity" onBack={onBack} />
        
        {/* Dynamic Header based on Available Data */}
        <GradientBackground color="primary" sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            {displayMetrics.map((metric, index) => (
              <Grid item xs={gridXs} sm={gridSm} md={gridMd} key={index}>
                <Typography variant="overline" color="text.secondary">{metric.label}</Typography>
                <Typography variant="h3" fontWeight={700} sx={{ color: metric.color }}>
                  {metric.value}
                  {metric.unit && (
                    <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                      {metric.unit}
                    </Typography>
                  )}
                </Typography>
                {metric.subtext && (
                  <Typography variant="caption" color="text.secondary">{metric.subtext}</Typography>
                )}
              </Grid>
            ))}
            {displayMetrics.length === 0 && (
               <Grid item xs={12}>
                 <Typography variant="body1" color="text.secondary">No volume or cardio data recorded.</Typography>
               </Grid>
            )}
          </Grid>
        </GradientBackground>

        {isWarmupOnly && totalSets > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Warmup / Light Session</AlertTitle>
            All {totalWarmupSets} sets were logged as warmup.
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <DetailSection title="Session Type" icon={<FitnessCenterIcon sx={{ fontSize: 18 }} />}>
              <Box sx={{ py: 1 }}>
                {/* Dominant Modality */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" fontWeight={700} color="primary.main" sx={{ textTransform: 'capitalize' }}>
                      {dominantModality}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">dominant modality</Typography>
                  </Box>
                </Box>

                {/* Modalities Used */}
                {biomechanics?.modalities_used && biomechanics.modalities_used.length > 0 && (
                  <Box sx={{
                    p: 1.5,
                    mb: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderRadius: 2,
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Modalities:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {biomechanics.modalities_used.map((modality) => (
                        <Chip
                          key={modality}
                          label={modality.replace(/_/g, ' ')}
                          size="small"
                          color={modality === 'strength' ? 'primary' :
                                 modality === 'cardio' ? 'secondary' :
                                 modality === 'flexibility' ? 'success' :
                                 modality === 'balance' ? 'info' : 'default'}
                          sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </DetailSection>
          </Grid>

          {/* Intensity Section */}
          <Grid item xs={12} sm={6}>
            <DetailSection title="Intensity" icon={<SpeedIcon sx={{ fontSize: 18 }} />} color="warning">
              <Box sx={{ py: 1 }}>
                
                {/* Cardio Intensity */}
                {(cardioMetrics?.average_pace_per_km_seconds > 0 || cardioMetrics?.average_heart_rate > 0) && (
                   <Box sx={{ mb: 2 }}>
                      {cardioMetrics.average_pace_per_km_seconds > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                           <Typography variant="body2" color="text.secondary">Avg Pace</Typography>
                           <Typography variant="body2" fontWeight="bold">
                              {new Date(cardioMetrics.average_pace_per_km_seconds * 1000).toISOString().substr(14, 5)} /km
                           </Typography>
                        </Box>
                      )}
                      {cardioMetrics.average_heart_rate > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                           <Typography variant="body2" color="text.secondary">Avg Heart Rate</Typography>
                           <Typography variant="body2" fontWeight="bold">
                              {cardioMetrics.average_heart_rate} bpm
                           </Typography>
                        </Box>
                      )}
                      {cardioMetrics.max_heart_rate > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                           <Typography variant="body2" color="text.secondary">Max Heart Rate</Typography>
                           <Typography variant="body2" fontWeight="bold">
                              {cardioMetrics.max_heart_rate} bpm
                           </Typography>
                        </Box>
                      )}
                   </Box>
                )}

                {/* Strength Intensity */}
                {intensityMetrics?.average_intensity_percent > 0 && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" fontWeight={700} color="warning.main">
                          {intensityMetrics.average_intensity_percent.toFixed(0)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">of estimated 1RM</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{
                        position: 'relative',
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.warning.main, 0.15),
                        overflow: 'hidden',
                      }}>
                        <Box sx={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${Math.min(intensityMetrics.average_intensity_percent, 100)}%`,
                          bgcolor: intensityMetrics.average_intensity_percent > 85 ? 'error.main' :
                                   intensityMetrics.average_intensity_percent > 70 ? 'warning.main' : 'success.main',
                          borderRadius: 4,
                          transition: 'width 0.3s ease',
                        }} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Light</Typography>
                        <Typography variant="caption" color="text.secondary">Moderate</Typography>
                        <Typography variant="caption" color="text.secondary">Heavy</Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {intensityMetrics?.dominant_zone && (
                  <Box sx={{
                    p: 1.5,
                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                    borderRadius: 2,
                    mt: 2
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Training Zone:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip
                        label={intensityMetrics.dominant_zone.replace(/_/g, ' ')}
                        size="small"
                        color="warning"
                        sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                      />
                    </Box>
                  </Box>
                )}
                
                {/* Fallback if no intensity data */}
                {!intensityMetrics?.average_intensity_percent && !cardioMetrics?.average_heart_rate && (
                   <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                     No intensity data available.
                   </Typography>
                )}
              </Box>
            </DetailSection>
          </Grid>
        </Grid>

        {biomechanics?.muscle_volume_distribution && Object.keys(biomechanics.muscle_volume_distribution).length > 0 && (
          <DetailSection title="Muscle Distribution" icon={<FitnessCenterIcon sx={{ fontSize: 18 }} />} color="secondary">
            <Box sx={{ maxWidth: 500 }}>
              {Object.entries(biomechanics.muscle_volume_distribution)
                .filter(([_, pct]) => pct > 0)
                .sort(([_, a], [__, b]) => b - a)
                .map(([muscle, percentage]) => (
                  <Box key={muscle} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
                    <Typography variant="body2" sx={{ minWidth: 100, color: 'text.secondary', textTransform: 'capitalize' }}>
                      {muscle.replace(/_/g, ' ')}
                    </Typography>
                    <Box sx={{ flex: 1, height: 8, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 1 }}>
                      <Box sx={{ height: '100%', width: `${percentage}%`, bgcolor: 'primary.main', borderRadius: 1 }} />
                    </Box>
                    <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'right', fontWeight: 500 }}>
                      {percentage.toFixed(0)}%
                    </Typography>
                  </Box>
                ))}
            </Box>
          </DetailSection>
        )}
      </Box>
    </Fade>
  );
};

VolumeIntensityDetail.propTypes = {
  volumeMetrics: PropTypes.object,
  intensityMetrics: PropTypes.object,
  cardioMetrics: PropTypes.object,
  biomechanics: PropTypes.object,
  onBack: PropTypes.func.isRequired,
};

export default VolumeIntensityDetail;